/**
 * Tron wallet provider — TronLink only.
 *
 * Chain-isolation invariant
 * -------------------------
 * This hook MUST never touch `window.ethereum` or `window.solana`. Each chain's
 * provider owns exactly one wallet surface. Leaking across providers would
 * expose the user's EVM identity during a Tron-only flow (or vice-versa), which
 * defeats InterNull's cross-chain privacy guarantee.
 *
 * Source-chain routing
 * --------------------
 * Pages call `getChainType(deposit.userAddress)` first, then look up the
 * provider for that chain. The Tron provider is read for Tron deposits and
 * Tron-signed keyshare requests; never as a fallback for "any connected wallet".
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const TronWalletContext = createContext(null);

export const useTronWallet = () => {
  const ctx = useContext(TronWalletContext);
  if (!ctx) {
    throw new Error('useTronWallet must be used within a TronWalletProvider');
  }
  return ctx;
};

/**
 * Get the TronWeb instance if TronLink is injected. Pure read — does not
 * request accounts. Returns null when TronLink isn't installed or the user
 * hasn't yet approved the dapp.
 */
const readTronWeb = () => {
  if (typeof window === 'undefined') return null;
  const tw = window.tronWeb;
  if (!tw || !tw.ready) return null;
  return tw;
};

/** Network ID from the TronLink full-node host. Maps to our chain config name. */
const inferNetworkName = (tronWeb) => {
  const host = (tronWeb?.fullNode?.host || '').toLowerCase();
  if (host.includes('nile')) return 'tron-nile';
  if (host.includes('shasta')) return 'tron-shasta';
  if (host.includes('trongrid.io')) return 'tron-mainnet';
  return null;
};

// SessionStorage key for the "user explicitly clicked Connect TronLink" flag.
// We use sessionStorage (not localStorage) so closing the tab resets to the
// no-auto-connect default — matching the landing-page UX where TronLink stays
// silent until the user opts in.
const TRON_SESSION_FLAG = 'tron_connected_session';

export const TronWalletProvider = ({ children }) => {
  const [address, setAddress] = useState(null); // base58 "T..." or null
  const [tronWeb, setTronWeb] = useState(null);
  const [network, setNetwork] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [installed, setInstalled] = useState(false); // is the extension present?
  // True after the user has clicked Connect at least once in this tab. Persisted
  // across navigation via sessionStorage so visiting /history doesn't drop the
  // connection state that was established on /deposit or /dashboard.
  const hasConnectedRef = useRef(
    typeof window !== 'undefined' && window.sessionStorage?.getItem(TRON_SESSION_FLAG) === '1'
  );
  const tronWebRef = useRef(null);

  // Refresh state from window.tronWeb. Safe to call repeatedly.
  // ONLY call this after the user has explicitly clicked Connect — reading
  // window.tronWeb before that triggers TronLink's auto-approve prompt even
  // on the landing page, which violates the "manual connect like MetaMask" UX.
  const refreshFromInjection = useCallback(() => {
    const tw = readTronWeb();
    if (!tw) {
      setAddress(null);
      setTronWeb(null);
      setNetwork(null);
      tronWebRef.current = null;
      return;
    }
    const newAddr = tw.defaultAddress?.base58 || null;
    setAddress((prev) => (prev !== newAddr ? newAddr : prev));
    setTronWeb(tw);
    setNetwork(inferNetworkName(tw));
    tronWebRef.current = tw;
  }, []);

  // On mount we ALWAYS check whether the extension is installed (passive read
  // of `window.tronLink`). We only read `window.tronWeb` (which surfaces the
  // address) when the user has already opted in this session — preventing the
  // landing-page auto-prompt while still keeping the connection alive across
  // navigation inside the app.
  useEffect(() => {
    const detectInstalled = () => {
      setInstalled(typeof window !== 'undefined' && !!window.tronLink);
    };
    detectInstalled();

    // If the user previously clicked Connect in this tab, restore their address
    // from the already-injected window.tronWeb. window.tronWeb is populated by
    // TronLink only after approval, so reading it here is silent (no popup).
    if (hasConnectedRef.current) {
      refreshFromInjection();
    }

    let polls = 0;
    const pollId = setInterval(() => {
      polls += 1;
      detectInstalled();
      // Once we know the user has connected before, keep refreshing until
      // tronWeb shows up (TronLink can inject lazily after page load).
      if (hasConnectedRef.current) refreshFromInjection();
      if ((typeof window !== 'undefined' && window.tronLink) || polls > 30) clearInterval(pollId);
    }, 100);

    // Listen for account / node changes, but only act on them after the user
    // has explicitly connected once. Otherwise an unrelated TronLink event
    // could materialise a session we never asked for.
    const onMessage = (event) => {
      if (!hasConnectedRef.current) return;
      if (!event?.data?.message) return;
      const action = event.data.message.action;
      if (action === 'setAccount' || action === 'setNode' || action === 'accountsChanged' || action === 'connect' || action === 'disconnect') {
        refreshFromInjection();
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
      clearInterval(pollId);
    };
  }, [refreshFromInjection]);

  /**
   * Connect: ask TronLink for permission, then refresh state. Returns the
   * base58 address on success, or throws with a user-readable message.
   */
  const connect = useCallback(async () => {
    if (typeof window === 'undefined' || !window.tronLink) {
      throw new Error(
        'TronLink not installed.\n\n' +
        'Install from https://www.tronlink.org/ and switch to the Nile testnet.'
      );
    }
    setConnecting(true);
    try {
      const resp = await window.tronLink.request({ method: 'tron_requestAccounts' });
      // TronLink returns { code, message }. Code 200 = approved, 4001 = user rejected.
      if (resp && resp.code && resp.code !== 200) {
        throw new Error(resp.message || `TronLink declined (code ${resp.code})`);
      }
      // window.tronWeb is now populated; refresh.
      // Brief wait + re-read because tronLink occasionally needs a tick to settle.
      await new Promise((r) => setTimeout(r, 100));
      hasConnectedRef.current = true;
      try { window.sessionStorage?.setItem(TRON_SESSION_FLAG, '1'); } catch (_) { /* no-op */ }
      refreshFromInjection();
      const tw = readTronWeb();
      const addr = tw?.defaultAddress?.base58;
      if (!addr) throw new Error('TronLink approved but no address available. Unlock the wallet and try again.');
      return addr;
    } finally {
      setConnecting(false);
    }
  }, [refreshFromInjection]);

  /**
   * Disconnect: clear local state. TronLink doesn't expose a programmatic
   * disconnect — the user has to lock the wallet in the extension UI — so this
   * only forgets our cached state and the next connect() will re-prompt.
   */
  const disconnect = useCallback(() => {
    setAddress(null);
    setTronWeb(null);
    setNetwork(null);
    tronWebRef.current = null;
    hasConnectedRef.current = false;
    try { window.sessionStorage?.removeItem(TRON_SESSION_FLAG); } catch (_) { /* no-op */ }
  }, []);

  /**
   * Sign an arbitrary message with the connected TronLink wallet. Returns the
   * Ethereum-format (0x-prefixed, 65-byte, recovery v=27/28) hex string —
   * compatible with the backend's `recoverSignerAddress` so Tron user
   * addresses are recoverable from keyshare-request signatures.
   */
  const signMessage = useCallback(async (message) => {
    const tw = tronWebRef.current || readTronWeb();
    if (!tw) throw new Error('TronLink not connected. Click "Connect TronLink" first.');
    // TronLink supports both `signMessageV2` (Ethereum-style personal_sign) and
    // the older `sign` for tx hashes. We use V2 because the backend expects the
    // EIP-191 prefix wrap.
    if (typeof tw.trx?.signMessageV2 !== 'function') {
      throw new Error('Your TronLink version is too old to sign messages. Update to v4.0+.');
    }
    return tw.trx.signMessageV2(message);
  }, []);

  /**
   * Trigger a smart contract call via TronLink. Caller passes the contract
   * address (T... base58 or 0x41-prefixed hex), the function signature, and
   * the parameters. Returns the broadcast tx hash (no receipt poll — that's
   * the caller's responsibility).
   */
  const triggerContract = useCallback(async (contractAddress, functionSelector, parameters, options = {}) => {
    const tw = tronWebRef.current || readTronWeb();
    if (!tw) throw new Error('TronLink not connected.');
    const sender = tw.defaultAddress?.base58;
    if (!sender) throw new Error('TronLink has no active account.');

    const {
      feeLimit = 150_000_000,  // 150 TRX
      callValue = 0,           // sun
    } = options;

    const built = await tw.transactionBuilder.triggerSmartContract(
      contractAddress,
      functionSelector,
      { feeLimit, callValue },
      parameters || [],
      sender,
    );
    if (!built?.result?.result) {
      throw new Error('triggerSmartContract failed: ' + JSON.stringify(built?.result));
    }
    const signed = await tw.trx.sign(built.transaction);
    const broadcast = await tw.trx.sendRawTransaction(signed);
    if (!broadcast?.result) {
      throw new Error('Broadcast rejected: ' + JSON.stringify(broadcast));
    }
    return signed.txID || broadcast.txid || broadcast.txID;
  }, []);

  const value = useMemo(
    () => ({
      address,
      connected: !!address,
      installed,
      connecting,
      network,
      tronWeb,
      connect,
      disconnect,
      signMessage,
      triggerContract,
    }),
    [address, installed, connecting, network, tronWeb, connect, disconnect, signMessage, triggerContract],
  );

  return <TronWalletContext.Provider value={value}>{children}</TronWalletContext.Provider>;
};

export default TronWalletProvider;
